import React, { useMemo } from 'react';
import { useAdminContext } from '../../context/AdminContext.js';
import { KpiCard } from '../../components/common/KpiCard.js';
import AdminDashboardCharts from './AdminDashboardCharts.js';
import { Briefcase, TrendingUp, Users, UserCheck, Clock, BeakerIcon, RefreshCw } from 'lucide-react';
import '../../styles/AdminDashboard.css';
import '../../styles/Skeleton.css'; // Import skeleton styles

const MS_IN_DAY = 24 * 60 * 60 * 1000;

const getTimestampMs = (value) => {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value;
    return null;
};

const computePeriodSums = (items, boundaries, getDate, getValue = () => 1) => {
    if (!Array.isArray(items) || !items.length) {
        return { current: 0, previous: 0 };
    }

    const { startCurrent, startPrevious } = boundaries;
    let current = 0;
    let previous = 0;

    items.forEach((item) => {
        const timestamp = getTimestampMs(getDate(item));
        if (timestamp == null) return;
        if (timestamp >= startCurrent) {
            current += getValue(item) || 0;
        } else if (timestamp >= startPrevious && timestamp < startCurrent) {
            previous += getValue(item) || 0;
        }
    });

    return { current, previous };
};

const buildTrendDescriptor = (current, previous) => {
    const label = 'vs. semana anterior';

    if (!current && !previous) {
        return { direction: 'neutral', text: '—', label };
    }

    const difference = current - previous;

    if (!previous) {
        if (difference > 0) {
            return { direction: 'up', text: 'Nuevo', label };
        }
        if (difference < 0) {
            return { direction: 'down', text: 'Baja', label };
        }
        return { direction: 'neutral', text: '0%', label };
    }

    const percentageChange = (difference / Math.abs(previous)) * 100;
    const rounded = Number.isFinite(percentageChange) ? percentageChange.toFixed(1) : '0.0';

    if (difference > 0) {
        return { direction: 'up', text: `+${rounded}%`, label };
    }
    if (difference < 0) {
        return { direction: 'down', text: `${rounded}%`, label };
    }
    return { direction: 'neutral', text: '0%', label };
};

// Skeleton component for loading state
const AdminDashboardSkeleton = () => (
    <div>
        <h2 className="section-title skeleton" style={{ width: '200px', height: '32px' }}>&nbsp;</h2>
        {[0, 1, 2].map((sectionIndex) => (
            <section className="kpi-section" key={sectionIndex}>
                <h3 className="kpi-section__title skeleton" style={{ width: '240px', height: '24px' }}>&nbsp;</h3>
                <div className="kpi-grid">
                    {[0, 1, 2].map((cardIndex) => (
                        <div
                            key={`${sectionIndex}-${cardIndex}`}
                            className="kpi-card skeleton"
                            style={{ height: '110px' }}
                        ></div>
                    ))}
                </div>
            </section>
        ))}
        <div className="skeleton" style={{ height: '400px', marginTop: '2rem' }}></div>
    </div>
);

const AdminDashboard = () => {
    const { allAgreements, users, financialLog, loading, agreementsSummary } = useAdminContext() || { allAgreements: [], users: [], financialLog: [], loading: true };

    const stats = useMemo(() => {
        // Return empty stats if loading or data is not ready
        if (loading || !allAgreements || !users) {
            return {
                totalInvested: 0,
                totalInvestedTest: 0,
                totalAccumulated: 0,
                userCount: 0,
                approvedCount: 0,
                approvedTestCount: 0,
                completedCount: 0,
                completedTestCount: 0,
                pendingCount: 0,
            };
        }

        const approvedAgreements = allAgreements.filter(a => a.status === 'aprobado');
        const approvedRealAgreements = approvedAgreements.filter(a => !a.isTest);
        const approvedTestAgreements = approvedAgreements.filter(a => a.isTest === true);

        const totalInvested = approvedRealAgreements.reduce((sum, a) => sum + (a.netInvestmentAmount || a.amount || 0), 0);
        const totalInvestedTest = approvedTestAgreements.reduce((sum, a) => sum + (a.netInvestmentAmount || a.amount || 0), 0);

        const totalAccumulated = approvedRealAgreements.reduce((totalProfit, a) => {
            const baseValue = a.netInvestmentAmount || a.amount || 0;
            const currentValue = a.currentValue || baseValue;
            const profit = (currentValue > baseValue) ? (currentValue - baseValue) : 0;
            return totalProfit + profit;
        }, 0);

        const completedAgreements = allAgreements.filter(a => a.status === 'completado');
        const completedRealAgreements = completedAgreements.filter(a => !a.isTest);
        const completedTestAgreements = completedAgreements.filter(a => a.isTest === true);

        const result = {
            totalInvested,
            totalInvestedTest,
            totalAccumulated,
            userCount: users.length,
            approvedCount: approvedRealAgreements.length,
            approvedTestCount: approvedTestAgreements.length,
            completedCount: completedRealAgreements.length,
            completedTestCount: completedTestAgreements.length,
            pendingCount: allAgreements.filter(a => a.status === 'pendiente').length
        };

        // Si tenemos resumen desde backend, preferir esos conteos para exactitud
        if (agreementsSummary && agreementsSummary.combined) {
            result.approvedCount = agreementsSummary.combined.approved.real || result.approvedCount;
            result.approvedTestCount = agreementsSummary.combined.approved.promo || result.approvedTestCount;
            result.completedCount = agreementsSummary.combined.completed.real || result.completedCount;
            result.completedTestCount = agreementsSummary.combined.completed.promo || result.completedTestCount;
        }

        return result;
    }, [allAgreements, users, loading, agreementsSummary]);

    const trendData = useMemo(() => {
        const now = Date.now();
        const boundaries = {
            startCurrent: now - (7 * MS_IN_DAY),
            startPrevious: now - (14 * MS_IN_DAY),
        };

        const agreements = Array.isArray(allAgreements) ? allAgreements : [];
        const financialEntries = Array.isArray(financialLog) ? financialLog : [];
        const userEntries = Array.isArray(users) ? users : [];

        const realApproved = agreements.filter(a => a.status === 'aprobado' && !a.isTest);
        const promoApproved = agreements.filter(a => a.status === 'aprobado' && a.isTest === true);
        const realCompleted = agreements.filter(a => a.status === 'completado' && !a.isTest);
        const promoCompleted = agreements.filter(a => a.status === 'completado' && a.isTest === true);
        const pendingAgreements = agreements.filter(a => a.status === 'pendiente');

        const investmentReal = computePeriodSums(
            realApproved,
            boundaries,
            (agreement) => agreement.approvedAt || agreement.reviewedAt || agreement.updatedAt || agreement.createdAt,
            (agreement) => agreement.netInvestmentAmount || agreement.amount || 0
        );

        const investmentPromo = computePeriodSums(
            promoApproved,
            boundaries,
            (agreement) => agreement.approvedAt || agreement.reviewedAt || agreement.updatedAt || agreement.createdAt,
            (agreement) => agreement.netInvestmentAmount || agreement.amount || 0
        );

        const approvedRealCounts = computePeriodSums(
            realApproved,
            boundaries,
            (agreement) => agreement.approvedAt || agreement.reviewedAt || agreement.updatedAt || agreement.createdAt
        );

        const approvedPromoCounts = computePeriodSums(
            promoApproved,
            boundaries,
            (agreement) => agreement.approvedAt || agreement.reviewedAt || agreement.updatedAt || agreement.createdAt
        );

        const completedRealCounts = computePeriodSums(
            realCompleted,
            boundaries,
            (agreement) => agreement.completedAt || agreement.reviewedAt || agreement.updatedAt
        );

        const completedPromoCounts = computePeriodSums(
            promoCompleted,
            boundaries,
            (agreement) => agreement.completedAt || agreement.reviewedAt || agreement.updatedAt
        );

        const pendingCounts = computePeriodSums(
            pendingAgreements,
            boundaries,
            (agreement) => agreement.createdAt || agreement.updatedAt
        );

        const userCounts = computePeriodSums(
            userEntries,
            boundaries,
            (user) => user.createdAt
        );

        const roiActivity = computePeriodSums(
            financialEntries.filter(entry => entry.type === 'ganancia_roi'),
            boundaries,
            (entry) => entry.createdAt,
            (entry) => entry.amount || 0
        );

        return {
            totalInvested: buildTrendDescriptor(investmentReal.current, investmentReal.previous),
            totalInvestedTest: buildTrendDescriptor(investmentPromo.current, investmentPromo.previous),
            approvedCount: buildTrendDescriptor(approvedRealCounts.current, approvedRealCounts.previous),
            approvedTestCount: buildTrendDescriptor(approvedPromoCounts.current, approvedPromoCounts.previous),
            completedCount: buildTrendDescriptor(completedRealCounts.current, completedRealCounts.previous),
            completedTestCount: buildTrendDescriptor(completedPromoCounts.current, completedPromoCounts.previous),
            pendingCount: buildTrendDescriptor(pendingCounts.current, pendingCounts.previous),
            userCount: buildTrendDescriptor(userCounts.current, userCounts.previous),
            totalAccumulated: buildTrendDescriptor(roiActivity.current, roiActivity.previous),
        };
    }, [allAgreements, financialLog, users]);

    const kpiSections = useMemo(() => ([
        {
            title: 'Rendimiento de inversión',
            items: [
                {
                    title: 'Capital Total Invertido (Real)',
                    value: stats.totalInvested.toFixed(2),
                    icon: <Briefcase size={24} />,
                    color: '#2563eb',
                    trend: trendData.totalInvested,
                },
                {
                    title: 'Resultados Acumulados (Real)',
                    value: stats.totalAccumulated.toFixed(2),
                    icon: <TrendingUp size={24} />,
                    color: '#16a34a',
                    trend: trendData.totalAccumulated,
                },
                {
                    title: 'Acuerdos Completados (Reales)',
                    value: stats.completedCount,
                    icon: <RefreshCw size={24} />,
                    color: '#0ea5e9',
                    trend: trendData.completedCount,
                },
            ],
        },
        {
            title: 'Usuarios y acuerdos activos',
            items: [
                {
                    title: 'Usuarios Registrados',
                    value: stats.userCount,
                    icon: <Users size={24} />,
                    color: '#9333ea',
                    trend: trendData.userCount,
                },
                {
                    title: 'Acuerdos Aprobados (Reales)',
                    value: stats.approvedCount,
                    icon: <UserCheck size={24} />,
                    color: '#16a34a',
                    trend: trendData.approvedCount,
                },
                {
                    title: 'Acuerdos Pendientes',
                    value: stats.pendingCount,
                    icon: <Clock size={24} />,
                    color: '#f59e0b',
                    trend: trendData.pendingCount,
                },
            ],
        },
        {
            title: 'Programas promocionales',
            items: [
                {
                    title: 'Capital Invertido (Promoción)',
                    value: stats.totalInvestedTest.toFixed(2),
                    icon: <BeakerIcon size={24} />,
                    color: '#64748b',
                    trend: trendData.totalInvestedTest,
                },
                {
                    title: 'Acuerdos de Promoción',
                    value: stats.approvedTestCount,
                    icon: <BeakerIcon size={24} />,
                    color: '#64748b',
                    trend: trendData.approvedTestCount,
                },
                {
                    title: 'Acuerdos Completados (Promoción)',
                    value: stats.completedTestCount,
                    icon: <RefreshCw size={24} />,
                    color: '#38bdf8',
                    trend: trendData.completedTestCount,
                },
            ],
        },
    ]), [stats, trendData]);

    if (loading) {
        return <AdminDashboardSkeleton />;
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="section-title">Vista General</h2>
            </div>
            {kpiSections.map((section) => (
                <section className="kpi-section" key={section.title}>
                    <h3 className="kpi-section__title">{section.title}</h3>
                    <div className="kpi-grid">
                        {section.items.map((item) => (
                            <KpiCard
                                key={item.title}
                                title={item.title}
                                value={item.value}
                                icon={item.icon}
                                color={item.color}
                            />
                        ))}
                    </div>
                </section>
            ))}
            <AdminDashboardCharts agreements={allAgreements} users={users} financialLog={financialLog} />
        </div>
    );
};

export default AdminDashboard;